
import { useState, useEffect } from "react"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { FormProgress } from "./form/FormProgress"
import { useLetter } from "@/hooks/useLetter"
import { useLetterSubmission } from "@/hooks/useLetterSubmission"
import { LetterContent } from "./letter/LetterContent"
import { FeedbackDialog } from "./letter/FeedbackDialog"
import { CloseConfirmDialog } from "./letter/CloseConfirmDialog"
import { OrderConfirmation } from "./letter/OrderConfirmation"

interface LetterEditorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  formData: any
}

export function LetterEditor({ open, onOpenChange, formData }: LetterEditorProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [showCloseAlert, setShowCloseAlert] = useState(false)
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false)
  const [orderComplete, setOrderComplete] = useState(false)

  const {
    letter,
    setLetter,
    isLoading,
    errorMessage,
    errorDetails,
    letterSource,
    retryCount,
    generateLetter,
    retryGeneration
  } = useLetter(formData)

  const {
    isSubmitting,
    emailSending,
    emailError,
    emailResponse,
    emailDebugInfo,
    handleSubmit
  } = useLetterSubmission(formData)

  useEffect(() => {
    if (open) {
      generateLetter()
    }
  }, [open])

  const handleClose = (open: boolean) => {
    if (!open) {
      if (orderComplete) {
        onOpenChange(false)
      } else {
        setShowCloseAlert(true)
      }
    }
  }

  const handleRegenerateLetter = () => {
    setShowFeedbackDialog(true)
  }

  const handleSubmitFeedback = (feedbackText: string) => {
    generateLetter(feedbackText)
  }

  const handleLetterSubmit = async () => {
    await handleSubmit(letter, () => {
      setOrderComplete(true)
    })
  }

  const handleReturnHome = () => {
    onOpenChange(false)
    // Optionally redirect to homepage if desired
    window.location.href = '/'
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
          {!orderComplete ? (
            <>
              <FormProgress currentStep={5} totalSteps={5} showPayment={true} />
              <p className="text-sm text-gray-600 mb-4 text-center">
                Review your letter below. You can edit it directly or click regenerate to create a new version with your feedback.
              </p>
              <div className="mt-4 space-y-4">
                <LetterContent
                  letter={letter}
                  isLoading={isLoading}
                  onLetterChange={setLetter}
                  onRegenerateClick={handleRegenerateLetter}
                  isEditing={isEditing}
                  onEditingChange={setIsEditing}
                  onSubmit={handleLetterSubmit}
                  isSubmitting={isSubmitting}
                  emailSending={emailSending}
                  errorMessage={errorMessage}
                  errorDetails={errorDetails}
                  letterSource={letterSource}
                  retryCount={retryCount}
                  onRetryClick={retryGeneration}
                  emailError={emailError}
                  emailResponse={emailResponse}
                  emailDebugInfo={emailDebugInfo}
                />
              </div>
            </>
          ) : (
            <OrderConfirmation 
              onReturn={handleReturnHome} 
              emailAddress={formData.sender.email} 
            />
          )}
        </DialogContent>
      </Dialog>

      <CloseConfirmDialog
        open={showCloseAlert}
        onOpenChange={setShowCloseAlert}
        onConfirm={() => {
          setShowCloseAlert(false)
          onOpenChange(false)
        }}
      />

      <FeedbackDialog
        open={showFeedbackDialog}
        onOpenChange={setShowFeedbackDialog}
        onSubmit={handleSubmitFeedback}
      />
    </>
  )
}
